import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  GraduationCap, BookOpen, Clock, Target, Sparkles, 
  ArrowRight, ArrowLeft, Check, Play, Zap, Brain, Star,
  Calculator, Beaker, Languages, Palette
} from "lucide-react";

const SUBJECTS = [
  { id: "math", name: "Mathematics", icon: Calculator, color: "bg-blue-500" },
  { id: "science", name: "Science", icon: Beaker, color: "bg-green-500" },
  { id: "english", name: "English", icon: Languages, color: "bg-purple-500" },
];

const LEARNING_PACES = [
  { id: "relaxed", name: "Relaxed", description: "1-2 sessions per week", icon: "🐢" },
  { id: "moderate", name: "Moderate", description: "3-4 sessions per week", icon: "🐇" },
  { id: "intensive", name: "Intensive", description: "Daily learning", icon: "🚀" },
];

const SESSION_DURATIONS = [
  { id: "15", name: "15 minutes", description: "Quick sessions" },
  { id: "30", name: "30 minutes", description: "Balanced learning" },
  { id: "45", name: "45 minutes", description: "Deep focus" },
];

export default function StudentOnboarding({ 
  studentName, 
  studentGrade, 
  onComplete, 
  onSkip 
}) {
  const [step, setStep] = useState(0);
  const [preferences, setPreferences] = useState({
    subjects: ["math"],
    learningPace: "moderate",
    sessionDuration: "30",
    completedTutorial: false
  });
  const [showSampleLesson, setShowSampleLesson] = useState(false);
  const [sampleLessonStep, setSampleLessonStep] = useState(0);

  const totalSteps = 4;
  const progress = ((step + 1) / totalSteps) * 100;

  const handleNext = () => {
    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      onComplete(preferences);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const toggleSubject = (subjectId) => {
    setPreferences(prev => ({
      ...prev,
      subjects: prev.subjects.includes(subjectId)
        ? prev.subjects.filter(s => s !== subjectId)
        : [...prev.subjects, subjectId]
    }));
  };

  // Sample lesson content for the demo
  const sampleLessonSteps = [
    {
      type: "lesson",
      title: "Welcome to Learning!",
      content: "Hi there! I'm your AI tutor. Let me show you how learning works here. We'll explore a quick concept together!",
      emoji: "👋"
    },
    {
      type: "lesson",
      title: "Learning by Doing",
      content: "Every topic starts with a simple explanation, then we practice together. If you get stuck, I'll help with hints!",
      emoji: "💡"
    },
    {
      type: "question",
      title: "Quick Practice",
      content: "Let's try! What is 3 + 5?",
      answer: "8",
      emoji: "🧮"
    },
    {
      type: "celebration",
      title: "Great Job!",
      content: "That's exactly right! You earned +10 XP! See how learning here is fun and rewarding?",
      emoji: "🎉"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl shadow-2xl border-0" data-testid="onboarding-card">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-between items-center mb-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onSkip}
              className="text-gray-500"
              data-testid="skip-onboarding"
            >
              Skip for now
            </Button>
            <span className="text-sm text-gray-500">Step {step + 1} of {totalSteps}</span>
          </div>
          <Progress value={progress} className="h-2 mb-6" />
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Step 0: Welcome */}
          {step === 0 && (
            <div className="text-center space-y-6" data-testid="step-welcome">
              <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                <Sparkles className="h-12 w-12 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Welcome, {studentName}!</h2>
                <p className="text-lg text-gray-600 mt-2">
                  Let's set up your personalized learning journey
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4 pt-4">
                <div className="p-4 bg-purple-50 rounded-xl text-center">
                  <Brain className="h-8 w-8 mx-auto text-purple-600 mb-2" />
                  <p className="text-sm font-medium">AI-Powered</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-xl text-center">
                  <Target className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                  <p className="text-sm font-medium">Personalized</p>
                </div>
                <div className="p-4 bg-green-50 rounded-xl text-center">
                  <Star className="h-8 w-8 mx-auto text-green-600 mb-2" />
                  <p className="text-sm font-medium">Gamified</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Subject Selection */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-subjects">
              <div className="text-center">
                <BookOpen className="h-12 w-12 mx-auto text-blue-600 mb-2" />
                <h2 className="text-2xl font-bold">Choose Your Subjects</h2>
                <p className="text-gray-600">Select the subjects you want to learn</p>
              </div>
              <div className="grid grid-cols-1 gap-4">
                {SUBJECTS.map(subject => {
                  const Icon = subject.icon;
                  const isSelected = preferences.subjects.includes(subject.id);
                  return (
                    <div
                      key={subject.id}
                      onClick={() => toggleSubject(subject.id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-purple-500 bg-purple-50 shadow-md' 
                          : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                      }`}
                      data-testid={`subject-${subject.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl ${subject.color} flex items-center justify-center`}>
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-lg">{subject.name}</p>
                          <p className="text-sm text-gray-500">Grade {studentGrade} curriculum</p>
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'border-purple-500 bg-purple-500' : 'border-gray-300'
                        }`}>
                          {isSelected && <Check className="h-4 w-4 text-white" />}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {preferences.subjects.length === 0 && (
                <p className="text-sm text-red-500 text-center">Please select at least one subject</p>
              )}
            </div>
          )}

          {/* Step 2: Learning Pace */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-pace">
              <div className="text-center">
                <Clock className="h-12 w-12 mx-auto text-orange-600 mb-2" />
                <h2 className="text-2xl font-bold">Set Your Pace</h2>
                <p className="text-gray-600">How often do you want to learn?</p>
              </div>
              <RadioGroup 
                value={preferences.learningPace}
                onValueChange={(value) => setPreferences(prev => ({ ...prev, learningPace: value }))}
                className="space-y-3"
              >
                {LEARNING_PACES.map(pace => (
                  <div key={pace.id} className="flex items-center space-x-3">
                    <RadioGroupItem value={pace.id} id={pace.id} data-testid={`pace-${pace.id}`} />
                    <Label 
                      htmlFor={pace.id} 
                      className="flex items-center gap-3 flex-1 p-4 rounded-xl border border-gray-200 cursor-pointer hover:border-purple-300 hover:bg-gray-50"
                    >
                      <span className="text-2xl">{pace.icon}</span>
                      <div>
                        <p className="font-medium">{pace.name}</p>
                        <p className="text-sm text-gray-500">{pace.description}</p>
                      </div>
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              <div className="border-t pt-6">
                <p className="text-sm text-gray-600 mb-3">Preferred session duration:</p>
                <div className="grid grid-cols-3 gap-3">
                  {SESSION_DURATIONS.map(duration => (
                    <Button
                      key={duration.id}
                      variant={preferences.sessionDuration === duration.id ? "default" : "outline"}
                      onClick={() => setPreferences(prev => ({ ...prev, sessionDuration: duration.id }))}
                      className={`h-auto py-3 ${
                        preferences.sessionDuration === duration.id ? 'bg-purple-600' : ''
                      }`}
                      data-testid={`duration-${duration.id}`}
                    >
                      <div className="text-center">
                        <p className="font-medium">{duration.name}</p>
                        <p className="text-xs opacity-75">{duration.description}</p>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Sample Lesson */}
          {step === 3 && (
            <div className="space-y-6" data-testid="step-sample">
              <div className="text-center">
                <Play className="h-12 w-12 mx-auto text-green-600 mb-2" />
                <h2 className="text-2xl font-bold">Try a Quick Demo</h2>
                <p className="text-gray-600">See how learning works here!</p>
              </div>

              {!showSampleLesson ? (
                <div className="text-center py-8">
                  <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center mb-6 shadow-lg">
                    <GraduationCap className="h-16 w-16 text-white" />
                  </div>
                  <Button 
                    size="lg"
                    onClick={() => setShowSampleLesson(true)}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                    data-testid="start-demo"
                  >
                    <Play className="h-5 w-5 mr-2" />
                    Start Demo Lesson
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-purple-100 to-indigo-100 rounded-xl p-6">
                    <div className="text-4xl mb-4">{sampleLessonSteps[sampleLessonStep].emoji}</div>
                    <h3 className="text-xl font-bold text-purple-900">
                      {sampleLessonSteps[sampleLessonStep].title}
                    </h3>
                    <p className="text-purple-700 mt-2">
                      {sampleLessonSteps[sampleLessonStep].content}
                    </p>
                    
                    {sampleLessonSteps[sampleLessonStep].type === "question" && (
                      <div className="mt-4 grid grid-cols-2 gap-2">
                        {["6", "8", "7", "9"].map(answer => (
                          <Button
                            key={answer}
                            variant="outline"
                            onClick={() => {
                              if (answer === "8") {
                                setSampleLessonStep(sampleLessonStep + 1);
                              }
                            }}
                            className="py-4 text-lg"
                            data-testid={`answer-${answer}`}
                          >
                            {answer}
                          </Button>
                        ))}
                      </div>
                    )}
                    
                    {sampleLessonSteps[sampleLessonStep].type === "celebration" && (
                      <div className="mt-4 flex items-center justify-center gap-2 text-yellow-600">
                        <Zap className="h-6 w-6" />
                        <span className="text-xl font-bold">+10 XP</span>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-center">
                    {sampleLessonStep < sampleLessonSteps.length - 1 && 
                     sampleLessonSteps[sampleLessonStep].type !== "question" && (
                      <Button
                        onClick={() => setSampleLessonStep(sampleLessonStep + 1)}
                        className="bg-purple-600"
                        data-testid="next-lesson-step"
                      >
                        Continue <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    )}
                    {sampleLessonStep === sampleLessonSteps.length - 1 && (
                      <div className="text-center">
                        <p className="text-green-600 font-medium mb-2">Demo Complete!</p>
                        <p className="text-sm text-gray-500">You're ready to start learning!</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6 border-t">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={step === 0}
              data-testid="btn-back"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={step === 1 && preferences.subjects.length === 0}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              data-testid="btn-next"
            >
              {step === totalSteps - 1 ? (
                <>
                  Start Learning <Sparkles className="h-4 w-4 ml-2" />
                </>
              ) : (
                <>
                  Next <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
